<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
	<button
		class="c-InputChip"
		:class="inputChipClassObj"
		@mousedown.prevent
		@keydown.delete="handleKeydown"
		@click="removeChip(true, $event)">
		<span class="c-InputChip__label">{{ label }}</span>
		<button
			v-if="withRemove"
			class="c-InputChip__removeBtn"
			@mousedown.prevent
			@click="removeChip(false, $event)">
			<i></i>
		</button>
	</button>
</template>

<script>
export default {
	name: 'InputChip',
	props: {
		/**
     * The InputChip label
     */
		label: {
			type: [Number, String],
			required: true,
			default: () => undefined
		},
		/**
     * The InputChip value
     */
		value: {
			type: [Number, String],
			required: true,
			default: () => undefined
		},
		/**
     * an extra styling class if it's needed to override the default styling for the InputChip
     */
		stylingClass: {
			type: String,
			required: false,
			default: () => ''
		},
		/**
     * If the InputChip can be removed (clicked)
     * @values true, false
     */
		withRemove: {
			type: Boolean,
			required: false,
			default: () => true
		},
		/**
     * If all InputChip is clickable, or just the X part
     * @values true, false
     */
		isAllChipClickable: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	computed: {
		inputChipClassObj () {
			const classObj = {
				withoutRemoveBtn: !this.withRemove
			}

			if (this.stylingClass) {
				classObj[`${this.stylingClass}`] = true
			}

			if (this.isAllChipClickable) {
				classObj.isAllChipClickable = true
			}

			return classObj
		}
	},
	methods: {
		removeChip (isFromLabel, ev) {
			if (!isFromLabel) {
				ev.stopPropagation()
			}

			if (!isFromLabel && this.withRemove) {
				/**
         * Triggers when InputChip is clicked
         * @event remove
         */
				this.$emit('remove')
			} else if (isFromLabel && this.withRemove && this.isAllChipClickable) {
				this.$emit('remove')
			}
		},
		handleKeydown (ev) {
			ev.preventDefault()
			this.removeChip(false, ev)
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-InputChip {
  height: 30px;
  border: none;
  border: 1px solid $un-n100;
  border-radius: calc(#{$border-radius} * 4);
  outline: none;
  background-color: $un-n100;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0 calc(#{$base-padding} / 2) 0 $base-padding;

  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;
  }

	&.withoutRemoveBtn {
		padding: 0 $base-padding;
		cursor: default !important;
	}

  &.isAllChipClickable {
    cursor: pointer;
  }

  &__label {
    font-family: $primary-font;
    font-size: 12px;
    line-height: 1.75;
    letter-spacing: 0.21px;
    color: $un-n900;
    user-select: none;
    white-space: nowrap;
  }

  &.isComplete {
    border: 1px solid $un-green-lighter;
    background-color: $un-green-lighter;

    span {
      color: $un-green-darker;
    }

    .c-InputChip__removeBtn i,
    .c-InputChip__removeBtn i:hover {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3E%3Cpath fill='%231B5E1F' fill-rule='evenodd' d='M12.117 5.881a.797.797 0 00-1.125 0L9.006 7.867 7.018 5.88A.798.798 0 005.89 7.009l1.985 1.986-1.985 1.986a.797.797 0 001.128 1.128l1.985-1.985 1.986 1.986a.799.799 0 001.125-1.128l-1.985-1.987 1.985-1.985a.8.8 0 00.003-1.13z'/%3E%3C/svg%3E");
    }
  }

  &.isStart {
    background-color: transparent;
    border: 1px solid $un-blue;

    span {
      color: $un-blue;
    }

    .c-InputChip__removeBtn i,
    .c-InputChip__removeBtn i:hover {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3E%3Cpath fill='%233843D0' fill-rule='evenodd' d='M12.117 5.881a.797.797 0 00-1.125 0L9.006 7.867 7.018 5.88A.798.798 0 005.89 7.009l1.985 1.986-1.985 1.986a.797.797 0 001.128 1.128l1.985-1.985 1.986 1.986a.799.799 0 001.125-1.128l-1.985-1.987 1.985-1.985a.8.8 0 00.003-1.13z'/%3E%3C/svg%3E");
    }
  }

  &.isFailed {
    background-color: $un-red;
    border: 1px solid $un-red;

    span {
      color: $un-white;
    }

    .c-InputChip__removeBtn i,
    .c-InputChip__removeBtn i:hover {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3E%3Cpath fill='%23FFFFFF' fill-rule='evenodd' d='M12.117 5.881a.797.797 0 00-1.125 0L9.006 7.867 7.018 5.88A.798.798 0 005.89 7.009l1.985 1.986-1.985 1.986a.797.797 0 001.128 1.128l1.985-1.985 1.986 1.986a.799.799 0 001.125-1.128l-1.985-1.987 1.985-1.985a.8.8 0 00.003-1.13z'/%3E%3C/svg%3E");
    }
  }

  &__removeBtn {
    border: none;
    outline: none;
    background-color: transparent;
    margin: 0;
    padding: 0;
    width: 24px;
    height: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover i {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3E%3Cpath fill='%233843D0' fill-rule='evenodd' d='M12.117 5.881a.797.797 0 00-1.125 0L9.006 7.867 7.018 5.88A.798.798 0 005.89 7.009l1.985 1.986-1.985 1.986a.797.797 0 001.128 1.128l1.985-1.985 1.986 1.986a.799.799 0 001.125-1.128l-1.985-1.987 1.985-1.985a.8.8 0 00.003-1.13z'/%3E%3C/svg%3E");
    }

    i {
      position: relative;
      width: 18px;
      height: 18px;
      display: block;
      background-repeat: no-repeat;
      background-size: cover;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3E%3Cpath fill='%23343941' fill-rule='evenodd' d='M12.117 5.881a.797.797 0 00-1.125 0L9.006 7.867 7.018 5.88A.798.798 0 005.89 7.009l1.985 1.986-1.985 1.986a.797.797 0 001.128 1.128l1.985-1.985 1.986 1.986a.799.799 0 001.125-1.128l-1.985-1.987 1.985-1.985a.8.8 0 00.003-1.13z'/%3E%3C/svg%3E");
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@unbabel/ui/src/components/InputChip/InputChip.vue